import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { AlertColor } from '@mui/material';
import { DataGrid, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import ColumnSelector from '~/components/Promo/PromoTester/ColumnSelector';

import { Voucher, getVoucherWarnings } from '~/services/PromoService';

import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import DebugModal from '../DebugModal/DebugModal';

type Props = {
  voucherName: string;
  setVoucherName: (v: string) => void;
  userEmail: string;
  setUserEmail: (v: string) => void;
  onlyActive: boolean;
  setOnlyActive: (v: boolean) => void;
  data: Array<Voucher>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onConfirmDelete: (voucher: Voucher) => Promise<void>;
  isDeleting: boolean;
};

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: true,
  disableColumnMenu: true,
  flex: 0.5,
};

function getVoucherWarningSeverity(level?: string): AlertColor {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
}

function getColumns({
  onDelete,
  onOpenWarnings,
}: {
  onDelete: (row: Voucher) => void;
  onOpenWarnings: (row: Voucher) => void;
}) {
  const columns: Array<GridColDef<Voucher>> = [
    {
      field: 'voucherId',
      headerName: 'ID',
      ...sharedOpts,
      renderCell: (params) => params.row.voucherId || '',
    },
    {
      field: 'voucherName',
      headerName: 'Voucher Name',
      ...sharedOpts,
      renderCell: (params) => params.row.voucherName || '',
    },
    {
      field: 'voucherTitle',
      headerName: 'Voucher Title',
      ...sharedOpts,
      renderCell: (params) => params.row.voucherTitle || '',
    },
    {
      field: 'parentVoucherId',
      headerName: 'Parent voucher',
      ...sharedOpts,
      valueGetter: (_value, row) => {
        const id = (row as Voucher & { clonedFromId?: string | null }).clonedFromId;
        return typeof id === 'string' ? id.trim() : '';
      },
      renderCell: (params) => {
        const id = params.value as string;
        if (!id) {
          return '';
        }
        return (
          <Button
            variant="text"
            size="small"
            component={Link}
            to={`/marketing/vouchers/${id}`}
            sx={{ p: 0, minWidth: 0 }}
          >
            {id}
          </Button>
        );
      },
    },
    {
      field: 'theme',
      headerName: 'Theme',
      ...sharedOpts,
      renderCell: (params) => params.row.theme || '',
    },
    {
      field: 'productType',
      headerName: 'Product Type',
      ...sharedOpts,
      renderCell: (params) => params.row.productType || '',
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      ...sharedOpts,
      renderCell: (params) => DateFormatterDetailed({ date: params.row.createdAt }),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      ...sharedOpts,
      renderCell: (params) => DateFormatterDetailed({ date: params.row.createdAt }),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      ...sharedOpts,
      hideable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" size="small" component={Link} to={`/marketing/vouchers/${params.row.voucherId}`}>
            View
          </Button>
          <Button variant="outlined" size="small" color="error" onClick={() => onDelete(params.row)}>
            Delete
          </Button>
        </Stack>
      ),
    },
    {
      field: 'debug',
      headerName: 'Debug',
      ...sharedOpts,
      renderCell: (params) => {
        const warnings = getVoucherWarnings(params.row);
        const warningCount = warnings.length;
        const warningsLabel = `${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`;
        return (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {warningCount === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {warningsLabel}
              </Typography>
            ) : (
              <Button variant="text" size="small" onClick={() => onOpenWarnings(params.row)}>
                {warningsLabel}
              </Button>
            )}
            <DebugModal type="generic" title="Debug" data={params.row} />
          </Stack>
        );
      },
    },
  ];
  return columns;
}

export default function VouchersTab({
  voucherName,
  setVoucherName,
  userEmail,
  setUserEmail,
  onlyActive,
  setOnlyActive,
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  onConfirmDelete,
  isDeleting,
}: Props) {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<Voucher | undefined>(undefined);
  const [warningsDialogVoucher, setWarningsDialogVoucher] = useState<Voucher | undefined>(undefined);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [voucherToClone, setVoucherToClone] = useState<Voucher | undefined>(undefined);
  const [userEmailInput, setUserEmailInput] = useState(userEmail);

  const rows = useMemo(() => data.map((row) => ({ id: row.voucherId, ...row })), [data]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    voucherId: false,
    parentVoucherId: false,
    createdAt: false,
    updatedAt: false,
  });

  useEffect(() => {
    setUserEmailInput(userEmail);
  }, [userEmail]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (userEmailInput !== userEmail) {
        setUserEmail(userEmailInput);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userEmailInput, userEmail, setUserEmail]);

  const handleDelete = useCallback((row: Voucher) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleOpenWarnings = useCallback((row: Voucher) => {
    setWarningsDialogVoucher(row);
  }, []);

  const handleCloseWarnings = useCallback(() => {
    setWarningsDialogVoucher(undefined);
  }, []);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setRowToDelete(undefined);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!rowToDelete) {
      return;
    }
    try {
      await onConfirmDelete(rowToDelete);
      cancelDelete();
    } catch (error) {
      // Keep dialog open if delete fails.
    }
  }, [cancelDelete, onConfirmDelete, rowToDelete]);

  const handleColumnVisibilityChange = useCallback((field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({ ...prev, [field]: visible }));
  }, []);

  const columns = useMemo(
    () => getColumns({ onDelete: handleDelete, onOpenWarnings: handleOpenWarnings }),
    [handleDelete, handleOpenWarnings],
  );

  const warningsDialogList = warningsDialogVoucher ? getVoucherWarnings(warningsDialogVoucher) : [];

  const Toolbar = useCallback(
    () => (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
        <ColumnSelector
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          size="small"
        />
      </Stack>
    ),
    [columnVisibilityModel, columns, handleColumnVisibilityChange],
  );

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid size={12} container spacing={2}>
          <Grid size={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                label="Voucher Name"
                value={voucherName}
                onChange={(e) => setVoucherName(e.target.value)}
                fullWidth
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                control={
                  <Switch checked={onlyActive} onChange={(_, checked) => setOnlyActive(checked)} color="primary" />
                }
                label="Active vouchers only"
              />
            </Stack>
          </Grid>
        </Grid>
        <Grid size={4} container spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="flex-end" width="100%">
            <Button variant="contained" color="primary" component={Link} to="/marketing/vouchers-create">
              New Voucher
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination, toolbar: Toolbar }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={rows}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.voucherId}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
        />
      </Box>
      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Voucher</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete voucher {rowToDelete?.voucherName || rowToDelete?.voucherId}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={cancelDelete} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmDelete} disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(warningsDialogVoucher)} onClose={handleCloseWarnings} maxWidth="sm" fullWidth>
        <DialogTitle>
          Voucher warnings
          {warningsDialogVoucher ? ` — ${warningsDialogVoucher.voucherName}` : ''}
        </DialogTitle>
        <DialogContent>
          {warningsDialogList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No warnings for this voucher.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {warningsDialogList.map((warning, index) => (
                <Alert
                  key={`${warning.code ?? 'warning'}-${index}`}
                  severity={getVoucherWarningSeverity(warning.level)}
                >
                  <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                  {warning.message}
                </Alert>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseWarnings}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
